import React, { useEffect, useState } from "react";
import { Select, Space, Grid } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/es";
import "dayjs/locale/ar";
import "dayjs/locale/pl";
import "dayjs/locale/ca";
import "dayjs/locale/mn";
import "dayjs/locale/ru";
import "dayjs/locale/uk";
import "dayjs/locale/zh";
const { useBreakpoint } = Grid;

const DateOfBirth = (props: any) => {
  const { handleDateChange, language } = props;
  const screens = useBreakpoint();
  const [dayInput, setDayInput] = useState(props?.dob?.[1] ?? "");
  const [highlightedYearIndex, setHighlightedYearIndex] = useState(-1);
  const [yearInput, setYearInput] = useState(props?.dob?.[2] ?? "");
  const [showDayOptions, setShowDayOptions] = useState(false);
  const [showYearOptions, setShowYearOptions] = useState(false);
  const [monthInput, setMonthInput] = useState(props?.dob?.[0] ?? "");
  const [showMonthOptions, setShowMonthOptions] = useState(false);

  let currentLanguage = language;
  if (currentLanguage === "rs") currentLanguage = "ru";
  else if (currentLanguage === "ur") currentLanguage = "uk";
  else if (currentLanguage === "ch") currentLanguage = "zh";

  
  useEffect(() => {
    if (["ru", "uk", "zh"].includes(currentLanguage)) {
      dayjs.locale(currentLanguage);
    }
  }, [currentLanguage]);
  const filteredDays = Array.from({ length: 31 }, (_, i) =>
    (i + 1).toString()
  ).filter((d) => d.startsWith(dayInput.toString()));

  const filteredYears = Array.from({ length: 100 }, (_, i) =>
    (dayjs().year() - i).toString()
  ).filter((y) => y.startsWith(yearInput.toString()));
  useEffect(() => {
    if (language) {
      dayjs.locale(language);
    }
  }, [language]);

  const displayFirstThreeLetters = (text: string) =>
    text ? text.substring(0, 3) : "";

  const filterOption = (
    input: string,
    option?: { label: string; value: string }
  ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const onSearch = (value: string) => console.log("search:", value);

  const getMonths = () => {
    return Array.from({ length: 12 }, (_, i) => {
      const monthName = dayjs().month(i).format("MMMM");
      return {
        key: i + 1,
        value: (i + 1).toString(),
        label: displayFirstThreeLetters(monthName),
      };
    });
  };

  const getMonthByKey = (keyValue: any) => {
    const months = getMonths();
    const monthObj = months.find((month) => month.value === keyValue);
    return monthObj ? monthObj.label : undefined;
  };
  const months = getMonths();
  const getMonthLabel = (value: string) =>
    months.find((m) => m.value === value)?.label ?? "";


  const filteredMonths = months.filter((m) =>
    m.label.toLowerCase().startsWith(monthInput.toLowerCase())
  );
  return (
    <div data-testid="input-dob">
      <Space>
        {/* Month - Select dropdown */}
        <div style={{ position: "relative" }}>
          <input
            type="text"
            aria-label="Birth month"
            autoComplete="bday-month"
            value={monthInput}
            onFocus={() => setShowMonthOptions(true)}
            onBlur={() => {
              setShowMonthOptions(false);

              const match = months.find(
                (m) => m.label.toLowerCase() === monthInput.toLowerCase()
              );

              if (match) {
                // If it's a valid month, update value again (optional)
                setMonthInput(match.label);
                handleDateChange(match.value, 0);
              } else {
                // Clear invalid input
                setMonthInput("");
                handleDateChange("", 0);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Tab" && filteredMonths.length > 0) {
                const firstMatch = filteredMonths[0];
                setMonthInput(firstMatch.label);
                handleDateChange(firstMatch.value, 0);
              }
            }}
            onChange={(e) => {
              const input = e.target.value;
              setMonthInput(input); // Store label, not value
              const match = months.find(
                (m) => m.label.toLowerCase() === input.toLowerCase()
              );
              if (match) {
                handleDateChange(match.value, 0);
              }
            }}
            style={{
              width: screens.md ? 120 : 80,
              height: 38,
              fontWeight: "500",
              textTransform: "capitalize",
              borderRadius: "6px",
              border: "1px solid #d9d9d9",
              textAlign: "center",
            }}
            data-testid="dob-mnth"
            id="dob-month"
            autoComplete="off"
          />
          {showMonthOptions && (
            <div
              style={{
                position: "absolute",
                top: 42,
                width: screens.md ? 120 : 80,
                maxHeight: 150,
                overflowY: "auto",
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                zIndex: 1000,
              }}
            >
              {filteredMonths.map((month) => (
                <div
                  key={month.value}
                  role="option"
                  tabIndex={-1}
                  aria-selected={monthInput === month.label}
                  style={{
                    padding: "4px 8px",
                    cursor: "pointer",
                    fontWeight: "500",
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault(); // keep focus on the input so blur doesn't fire before this selection registers
                    setMonthInput(month.label); // Set label as input value
                    handleDateChange(month.value, 0); // But pass value to handler
                    setShowMonthOptions(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setMonthInput(month.label);
                      handleDateChange(month.value, 0);
                    }
                  }}
                >
                  {month.label.toString()}
                </div>
              ))}
              {filteredMonths.length === 0 && (
                <div
                  style={{
                    padding: "4px 8px",
                    color: "#a1a1a1",
                    fontWeight: 600,
                  }}
                >
                  No match
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ position: "relative" }}>
          <input
            type="number"
            min="1"
            max="31"
            aria-label="Birth day"
            autoComplete="bday-day"
            value={dayInput}
            onFocus={() => setShowDayOptions(true)}
            onBlur={() => {
              setShowDayOptions(false);

              // Check if input is a valid day (1-31), allowing leading zeros (e.g. "07")
              const numericDay = parseInt(dayInput.toString(), 10);
              const isValid =
                dayInput !== "" &&
                /^\d{1,2}$/.test(dayInput.toString()) &&
                numericDay >= 1 &&
                numericDay <= 31;
              if (!isValid) {
                setDayInput("");
                handleDateChange("", 1); // Clear invalid input
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Tab" && filteredDays.length > 0) {
                const firstMatch = filteredDays[0];
                setDayInput(firstMatch);
                handleDateChange(firstMatch, 1);
              }
            }}
            onChange={(e) => {
              const value = e.target.value;
              setDayInput(value);
              handleDateChange(value, 1);
            }}
            style={{
              width: screens.md ? 120 : 80,
              height: 38,
              fontWeight: "500",
              borderRadius: "6px",
              border: "1px solid #d9d9d9",
              textAlign: "center",
            }}
            inputMode="numeric"
            pattern="[0-9]*"
            data-testid="dob-date"
            id="dob-date"
            // placeholder="Day"
          />
          {showDayOptions && (
            <div
              style={{
                position: "absolute",
                top: 42,
                width: screens.md ? 120 : 80,
                maxHeight: 150,
                overflowY: "auto",
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                zIndex: 1000,
              }}
            >
              {filteredDays.map((day) => (
                <div
                  key={day}
                  role="option"
                  tabIndex={-1}
                  aria-selected={dayInput === day}
                  style={{
                    padding: "4px 8px",
                    cursor: "pointer",
                    fontWeight: "500",
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault(); // keep focus on the input so blur doesn't fire before this selection registers
                    setDayInput(day);
                    handleDateChange(day, 1);
                    setShowDayOptions(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDayInput(day);
                      handleDateChange(day, 1);
                    }
                  }}
                >
                  {day}
                </div>
              ))}
              {filteredDays.length === 0 && (
                <div
                  style={{
                    padding: "4px 8px",
                    color: "#a1a1a1",
                    fontWeight: 600,
                  }}
                >
                  No match
                </div>
              )}
            </div>
          )}
        </div>

        {/* Year Input + Searchable Dropdown */}
       <div style={{ position: "relative" }}>
  <input
    type="text"
    aria-label="Birth year"
    autoComplete="bday-year"
    value={yearInput}
    onFocus={() => {
      setShowYearOptions(true);
      setHighlightedYearIndex(-1);
    }}
    onBlur={() => {
      setShowYearOptions(false);
      setHighlightedYearIndex(-1);
      const isValid = filteredYears.includes(yearInput);
      if (!isValid) {
        setYearInput("");
        handleDateChange("", 2);
      }
    }}
    onChange={(e) => {
      const value = e.target.value;
      setYearInput(value);
      setHighlightedYearIndex(-1);
      handleDateChange(value, 2);
    }}
    onKeyDown={(e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (filteredYears.length > 0) {
          setShowYearOptions(true);
          setHighlightedYearIndex((prev) =>
            prev < filteredYears.length - 1 ? prev + 1 : 0
          );
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (filteredYears.length > 0) {
          setShowYearOptions(true);
          setHighlightedYearIndex((prev) =>
            prev > 0 ? prev - 1 : filteredYears.length - 1
          );
        }
      } else if (e.key === "Enter") {
        if (highlightedYearIndex >= 0 && filteredYears[highlightedYearIndex]) {
          e.preventDefault();
          const selected = filteredYears[highlightedYearIndex];
          setYearInput(selected);
          handleDateChange(selected, 2);
          setShowYearOptions(false);
          setHighlightedYearIndex(-1);
        }
      } else if (e.key === "Tab" && filteredYears.length > 0) {
        const chosen =
          highlightedYearIndex >= 0
            ? filteredYears[highlightedYearIndex]
            : filteredYears[0];
        setYearInput(chosen);
        handleDateChange(chosen, 2);
      } else if (e.key === "Escape") {
        setShowYearOptions(false);
        setHighlightedYearIndex(-1);
      }
    }}
    style={{
      width: screens.md ? 120 : 80,
      height: 38,
      fontWeight: "500",
      borderRadius: "6px",
      border: "1px solid #d9d9d9",
      textAlign: "center",
    }}
    inputMode="numeric"
    pattern="[0-9]*"
    data-testid="dob-year"
    id="dob-year"
  />
  {showYearOptions && (
    <div
      style={{
        position: "absolute",
        top: 42,
        width: screens.md ? 120 : 80,
        maxHeight: 150,
        overflowY: "auto",
        backgroundColor: "#fff",
        border: "1px solid #d9d9d9",
        zIndex: 1000,
      }}
    >
      {filteredYears.map((year, index) => (
        <div
          key={year}
          role="option"
          tabIndex={-1}
          aria-selected={yearInput === year}
          style={{
            padding: "4px 8px",
            cursor: "pointer",
            fontWeight: "500",
            backgroundColor:
              index === highlightedYearIndex ? "#f0f0f0" : "transparent",
          }}
          onMouseEnter={() => setHighlightedYearIndex(index)}
          onMouseDown={(e) => {
            e.preventDefault(); // keep focus on the input so blur doesn't fire before this selection registers
            setYearInput(year);
            handleDateChange(year, 2);
            setShowYearOptions(false);
          }}
        >
          {year}
        </div>
      ))}
      {filteredYears.length === 0 && (
        <div style={{ padding: "4px 8px", color: "#a1a1a1", fontWeight: 600 }}>
          No match
        </div>
      )}
    </div>
  )}
</div>
      </Space>
    </div>
  );
};

export default DateOfBirth;